import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { 
  ArrowRight, 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Palette, 
  Globe2, 
  Smartphone, 
  Zap,
  Check,
  Menu,
  X,
  Heart,
  MessageCircle,
  Shield,
  Sparkles,
  ShoppingCart,
  Settings,
  Building2
} from "lucide-react";
import axios from "axios";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  }
};

// Section component with scroll animation
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { scrollY } = useScroll();
  const headerBg = useTransform(
    scrollY,
    [0, 100],
    ["rgba(5, 5, 5, 0)", "rgba(5, 5, 5, 0.9)"]
  );
  const headerBlur = useTransform(scrollY, [0, 100], ["blur(0px)", "blur(20px)"]);

  const navItems = ["Services", "Portfolio", "About", "Contact"];

  return (
    <motion.header
      style={{ backgroundColor: headerBg, backdropFilter: headerBlur }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-white/5"
      data-testid="header"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.a
            href="/"
            className="flex items-center"
            whileHover={{ scale: 1.02 }}
            data-testid="logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_prestige-digital-1/artifacts/l5w9fjoc_WhatsApp%20Image%202026-03-08%20at%2023.43.08.jpeg" 
              alt="Webcraft" 
              className="h-8 md:h-10 w-auto rounded"
            />
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
            {navItems.map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="nav-link"
                data-testid={`nav-${item.toLowerCase()}`}
              >
                {item}
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <motion.a
            href="#contact"
            className="hidden md:inline-flex cta-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            data-testid="header-cta"
          >
            Start Your Project
          </motion.a>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="md:hidden bg-[#0A0A0A] border-t border-white/5"
          data-testid="mobile-menu"
        >
          <div className="container mx-auto px-6 py-6">
            {navItems.map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="block py-3 text-lg text-white/70 hover:text-white transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {item}
              </a>
            ))}
            <a
              href="#contact"
              className="block mt-4 cta-button text-center"
              onClick={() => setIsMenuOpen(false)}
            >
              Start Your Project
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

// Hero Section
const HeroSection = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  const trustBadges = [
    { icon: <Palette size={16} />, text: "Modern Design" },
    { icon: <Smartphone size={16} />, text: "Mobile Optimized" },
    { icon: <Zap size={16} />, text: "Built for Conversion" }
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background */}
      <motion.div 
        style={{ y }}
        className="absolute inset-0 z-0"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/50 to-[#050505] z-10" />
        <img
          src="https://images.unsplash.com/photo-1694843689189-2ad1a6c4a364?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBkaWdpdGFsJTIwd2F2ZXMlMjBsdXh1cnklMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MzAyNjAzM3ww&ixlib=rb-4.1.0&q=85"
          alt="Abstract dark luxury background"
          className="w-full h-full object-cover opacity-40"
        />
      </motion.div>

      {/* Hero Glow */}
      <div className="hero-glow z-0" />

      {/* Content */}
      <motion.div 
        style={{ opacity }}
        className="container mx-auto px-6 md:px-12 relative z-10 pt-24"
      >
        <div className="max-w-5xl mx-auto text-center">
          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-3 mb-10"
            data-testid="trust-badges"
          >
            {trustBadges.map((badge, index) => (
              <motion.div
                key={index}
                className="trust-badge"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
              >
                {badge.icon}
                <span>{badge.text}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold tracking-tighter leading-[0.95] mb-8"
            data-testid="hero-headline"
          >
            We Build Digital Presence
            <br />
            <span className="text-white/50">That Commands </span>
            <span className="font-accent italic text-white">Attention</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="text-base md:text-lg lg:text-xl text-white/50 max-w-2xl mx-auto mb-12 leading-relaxed"
            data-testid="hero-subheadline"
          >
            Webcraft designs premium websites that make businesses look established, trusted, and impossible to ignore.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
            data-testid="hero-cta-buttons"
          >
            <motion.a
              href="#portfolio"
              className="cta-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              data-testid="view-portfolio-btn"
            >
              View Portfolio
              <ArrowRight size={18} />
            </motion.a>
            <motion.a
              href="#contact"
              className="cta-button cta-button-secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              data-testid="start-project-btn"
            >
              Start Your Project
            </motion.a>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-white/50 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Premium Statement Section
const PremiumStatement = () => {
  return (
    <AnimatedSection className="section bg-[#050505]" data-testid="premium-statement-section">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div variants={fadeInUp} className="max-w-5xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-8">The Truth</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white mb-8" data-testid="premium-statement-headline">
            A business without a strong website already loses trust
            <span className="text-white/40"> before the first conversation begins.</span>
          </h2>
          <motion.p 
            variants={fadeInUp}
            className="text-base md:text-lg text-white/50 max-w-2xl mx-auto leading-relaxed"
            data-testid="premium-statement-text"
          >
            In today's digital landscape, your website is often your first impression. Webcraft helps companies 
            project strength, authority, and professionalism through strategic design that converts visitors into clients.
          </motion.p>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Who We Are Section
const WhoWeAreSection = () => {
  return (
    <AnimatedSection className="section bg-[#0A0A0A]" id="whoweare" data-testid="who-we-are-section">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Content */}
          <motion.div variants={fadeInUp}>
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">About Us</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-8" data-testid="who-we-are-headline">
              Who We Are
            </h2>
            
            <div className="space-y-6">
              <p className="text-white/60 leading-relaxed">
                Webcraft is a company specializing in creating custom websites for businesses that want to strengthen their online presence and stand out in a competitive digital environment. We design modern, high-performing websites tailored to each client's specific needs, with a focus on design, user experience, and search engine visibility.
              </p>
              
              <p className="text-white/60 leading-relaxed">
                From simple showcase websites to complete online stores, Webcraft offers customized web solutions optimized for mobile devices and designed to convert visitors into customers. We also support our clients with maintenance, redesign, and technical support services to ensure the performance and longevity of their websites.
              </p>
              
              <div className="glass-card p-6 mt-8">
                <p className="text-white/40 text-xs uppercase tracking-[0.2em] mb-3">Our Mission</p>
                <p className="text-white/80 leading-relaxed font-medium">
                  At Webcraft, our mission is to help businesses build a professional online image through reliable, accessible, and growth-oriented web solutions.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right: Visual Elements */}
          <motion.div variants={scaleIn} className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="glass-card p-6 text-center">
                  <div className="text-3xl font-extrabold text-white mb-2">100%</div>
                  <p className="text-white/40 text-xs uppercase tracking-widest">Custom Design</p>
                </div>
                <div className="glass-card p-6 text-center">
                  <div className="text-3xl font-extrabold text-white mb-2">24/7</div>
                  <p className="text-white/40 text-xs uppercase tracking-widest">Support</p>
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="glass-card p-6 text-center">
                  <div className="text-3xl font-extrabold text-white mb-2">SEO</div>
                  <p className="text-white/40 text-xs uppercase tracking-widest">Optimized</p>
                </div>
                <div className="glass-card p-6 text-center">
                  <div className="text-3xl font-extrabold text-white mb-2">Mobile</div>
                  <p className="text-white/40 text-xs uppercase tracking-widest">First Design</p>
                </div>
              </div>
            </div>
            
            {/* Decorative gradient */}
            <div className="absolute -inset-4 bg-gradient-to-r from-white/5 to-transparent rounded-3xl -z-10 blur-xl opacity-50" />
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Service Card Component
const ServiceCard = ({ service, index }) => {
  return (
    <Link to={`/services/${service.slug}`} className="block">
      <motion.div
        variants={scaleIn}
        className={`glass-card group cursor-pointer transition-all duration-500 h-full ${
          service.featured ? 'border-white/20 relative overflow-hidden' : ''
        }`}
        data-testid={`service-card-${index}`}
      >
        {service.featured && (
          <div className="absolute top-0 right-0 bg-white text-black text-xs font-bold px-4 py-1 rounded-bl-lg">
            POPULAR
          </div>
        )}
        
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center bg-white/5 rounded-xl group-hover:bg-white/10 transition-colors duration-500">
                {service.icon}
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-white">{service.title}</h3>
                {service.pages && (
                  <p className="text-xs text-white/40 mt-1">{service.pages}</p>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl md:text-3xl font-extrabold text-white">${service.price}</p>
              {service.period && (
                <p className="text-xs text-white/40">{service.period}</p>
              )}
            </div>
          </div>

          {/* Description */}
          <p className="text-white/50 text-sm leading-relaxed mb-6">
            {service.description}
          </p>

          {/* Features - Show first 4 */}
          <div className="space-y-3">
            {service.features.slice(0, 4).map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <span className="text-white/60 mt-0.5">✦</span>
                <p className="text-white/70 text-sm leading-relaxed">{feature}</p>
              </div>
            ))}
            {service.features.length > 4 && (
              <p className="text-white/40 text-sm">+ {service.features.length - 4} more features</p>
            )}
          </div>

          {/* CTA */}
          <div className="mt-6 w-full bg-white text-black px-6 py-3 rounded-full font-bold flex items-center justify-center gap-2 group-hover:bg-white/90 transition-colors text-sm">
            Learn More
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      slug: "showcase-site",
      icon: <Globe2 className="w-6 h-6 text-white/80" />,
      title: "Showcase Site",
      price: "499",
      pages: "Up to 5 pages",
      description: "At Webcraft, each website is custom-designed to accurately reflect your company's identity. We prioritize modern design, intuitive navigation, and an efficient structure to provide a smooth and professional user experience.",
      features: [
        "A personalized and professional design",
        "A clear presentation of your company and services",
        "Simple and intuitive navigation",
        "Full compatibility on mobile, tablet, and computer",
        "Basic search engine optimization (SEO)",
        "A functional contact page to facilitate communication"
      ],
      bottomText: "Creating a website with Webcraft provides you with a reliable and elegant presentation tool, designed to enhance your credibility and give your business a professional online presence."
    },
    {
      slug: "website-redesign",
      icon: <Palette className="w-6 h-6 text-white/80" />,
      title: "Website Redesign",
      price: "399",
      description: "At Webcraft, every redesign is custom-designed. We thoroughly analyze your brand image, business objectives, and user behavior to create a website with refined aesthetics, optimized for speed, security, and SEO.",
      features: [
        "A modern, high-end design consistent with your brand identity",
        "An intuitive and engaging user experience",
        "Optimal performance on all devices",
        "An advanced SEO structure for increased visibility",
        "A website designed to convert and inspire trust"
      ],
      bottomText: "A website redesign is a strategic investment that transforms your site into a true growth driver, reflecting the excellence of your business."
    },
    {
      slug: "website-business",
      icon: <Building2 className="w-6 h-6 text-white/80" />,
      title: "Website Business",
      price: "899",
      pages: "Up to 10 pages",
      featured: true,
      description: "At Webcraft, every business website is custom-designed to reflect your company's identity and meet your business objectives. We create structured, elegant, and high-performing websites designed to inspire trust and facilitate contact with your clients.",
      features: [
        "A professional and personalized design that reflects your brand",
        "A clear structure that showcases your services and expertise",
        "Smooth navigation and an optimized user experience",
        "Full compatibility with mobile, tablet, and desktop devices",
        "Basic search engine optimization (SEO)",
        "An effective contact form for lead generation"
      ],
      bottomText: "Creating a business website with Webcraft provides you with a powerful, reliable, and scalable communication tool designed to support your company's growth and credibility."
    },
    {
      slug: "website-ecommerce",
      icon: <ShoppingCart className="w-6 h-6 text-white/80" />,
      title: "Website E-commerce",
      price: "1,499",
      pages: "Unlimited pages",
      description: "At Webcraft, we design custom online stores, combining premium design, intuitive navigation, and reliable technologies. Each project is designed to showcase your products, simplify the customer journey, and ensure efficient management of sales, payments, and orders.",
      features: [
        "A modern, elegant online store tailored to your brand",
        "A seamless shopping experience on desktop, tablet, and mobile",
        "Secure payments and comprehensive product management",
        "A structure optimized for performance and SEO",
        "A website designed to convert and scale with your business"
      ],
      bottomText: "Creating an e-commerce website with Webcraft transforms your online presence into a powerful, reliable sales channel ready to support your business growth."
    },
    {
      slug: "monthly-maintenance",
      icon: <Settings className="w-6 h-6 text-white/80" />,
      title: "Monthly Maintenance",
      price: "49",
      period: "/month",
      description: "At Webcraft, monthly maintenance ensures your website remains consistently up-to-date, functional, and optimized. We handle technical updates, security monitoring, backups, and any necessary adjustments to maintain peak performance.",
      features: [
        "Regular website and feature updates",
        "Security vulnerability monitoring and protection",
        "Automatic backups and rapid restoration in case of issues",
        "Continuous performance and speed optimization",
        "Technical support and peace of mind"
      ],
      bottomText: "Monthly maintenance is a proactive solution that allows you to focus on your business, knowing your website is in good hands, secure, and always performing at its best."
    }
  ];

  return (
    <AnimatedSection className="section bg-[#050505]" id="services" data-testid="services-section">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Our Packages</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-4" data-testid="services-headline">
            Premium Services
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto">
            Transparent pricing. Exceptional quality. Every project is custom-designed to elevate your business.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              service={service}
              index={index}
            />
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// Portfolio Section
const PortfolioSection = () => {
  const portfolioItems = [
    {
      title: "Prestige Coffee Co.",
      category: "E-Commerce",
      image: "https://customer-assets.emergentagent.com/job_prestige-digital-1/artifacts/h9bf2217_WhatsApp%20Image%202026-03-08%20at%2023.05.25.jpeg"
    },
    {
      title: "Urban Brew Lab",
      category: "Hospitality",
      image: "https://customer-assets.emergentagent.com/job_prestige-digital-1/artifacts/q07mjio5_WhatsApp%20Image%202026-03-08%20at%2023.07.29.jpeg"
    },
    {
      title: "The Glass House",
      category: "Fine Dining",
      image: "https://customer-assets.emergentagent.com/job_prestige-digital-1/artifacts/lcygj8rg_WhatsApp%20Image%202026-03-08%20at%2023.09.22.jpeg"
    },
    {
      title: "Bistro Noir",
      category: "Restaurant",
      image: "https://customer-assets.emergentagent.com/job_prestige-digital-1/artifacts/7tnodi26_WhatsApp%20Image%202026-03-08%20at%2023.11.53.jpeg"
    }
  ];

  return (
    <AnimatedSection className="section bg-[#0A0A0A]" id="portfolio" data-testid="portfolio-section">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Our Work</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white" data-testid="portfolio-headline">
            Portfolio Showcase
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="portfolio-grid">
          {portfolioItems.map((item, index) => (
            <motion.div
              key={index}
              variants={scaleIn}
              className="portfolio-card group cursor-pointer"
              data-testid={`portfolio-item-${index}`}
            >
              <div className="relative overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">{item.category}</p>
                    <h3 className="text-xl font-bold text-white">{item.title}</h3>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Sarah Mitchell",
      role: "Owner",
      company: "Bella Rosa Spa",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      text: "Webcraft transformed our online presence completely. Our new website perfectly captures the luxury and relaxation our spa offers. Since launching, we've seen a 40% increase in online bookings. The team was professional, attentive, and delivered beyond our expectations."
    },
    {
      name: "Michael Chen",
      role: "Founder",
      company: "Urban Bistro",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      text: "Working with Webcraft was the best decision for our restaurant. They created a stunning website that showcases our menu beautifully and makes reservations seamless. Our customers constantly compliment how elegant and easy-to-use it is. Highly recommend!"
    },
    {
      name: "Jennifer Adams",
      role: "CEO",
      company: "Summit Construction",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      text: "As a construction company, we needed a website that conveyed trust and professionalism. Webcraft delivered exactly that. The portfolio showcase they built helps us win new contracts regularly. The investment paid for itself within the first month."
    },
    {
      name: "David Thompson",
      role: "Owner",
      company: "Thompson Legal Services",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      text: "Our law firm needed a website that projected authority and trustworthiness. Webcraft understood our vision perfectly and created a site that has significantly improved our client acquisition. The ongoing support has been exceptional."
    },
    {
      name: "Emma Rodriguez",
      role: "Founder",
      company: "Artisan Coffee House",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      text: "Webcraft created a beautiful website that truly reflects our café's personality. The online ordering system they integrated works flawlessly, and our customers love the experience. Sales have increased significantly since launch!"
    },
    {
      name: "Robert Williams",
      role: "Director",
      company: "Elite Fitness Studio",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      text: "The team at Webcraft built us a dynamic website with class booking integration that has streamlined our entire operation. New members consistently mention how impressed they are with our online presence. Worth every penny!"
    }
  ];

  return (
    <AnimatedSection className="section bg-[#050505]" id="testimonials" data-testid="testimonials-section">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Client Stories</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-4" data-testid="testimonials-headline">
            What Our Clients Say
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto">
            Don't just take our word for it. Here's what business owners have to say about working with Webcraft.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={scaleIn}
              className="glass-card p-6 md:p-8 group hover:border-white/20 transition-all duration-500"
              data-testid={`testimonial-${index}`}
            >
              {/* Rating Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>

              {/* Testimonial Text */}
              <p className="text-white/70 leading-relaxed mb-6 text-sm md:text-base">
                "{testimonial.text}"
              </p>

              {/* Author Info */}
              <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="text-white font-semibold">{testimonial.name}</p>
                  <p className="text-white/40 text-sm">{testimonial.role}, {testimonial.company}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// Why Choose Section
const WhyChooseSection = () => {
  const reasons = [
    { number: "01", label: "First Impression Matters" },
    { number: "02", label: "Trust Increases Instantly" },
    { number: "03", label: "Stronger Online Image" },
    { number: "04", label: "More Customer Inquiries" },
    { number: "05", label: "Competitive Advantage" }
  ];

  return (
    <AnimatedSection className="section bg-[#050505]" data-testid="why-choose-section">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">The Difference</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white" data-testid="why-choose-headline">
            Why Clients Choose Webcraft
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4" data-testid="why-choose-grid">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="why-item glass-card p-6"
              data-testid={`why-item-${index}`}
            >
              <div className="number text-2xl sm:text-3xl">{reason.number}</div>
              <div className="label text-xs sm:text-sm">{reason.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// Our Values Section
const OurValuesSection = () => {
  const values = [
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "Transparent Communication",
      description: "We believe in listening, clarity, and genuine commitment to our clients' success."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Customized Solutions",
      description: "Each project is designed with rigor and attention to detail, tailored to your unique needs."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Integrity & Support",
      description: "We prioritize ongoing support and build websites that inspire confidence and deliver real results."
    }
  ];

  return (
    <AnimatedSection className="section bg-[#0A0A0A]" id="values" data-testid="values-section">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">What Drives Us</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-6" data-testid="values-headline">
            Our Values
          </h2>
        </motion.div>

        {/* Main Statement */}
        <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/5 rounded-2xl mb-8">
            <Heart className="w-8 h-8 text-white/80" />
          </div>
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">
            At Webcraft, we put <span className="font-accent italic">people</span> at the heart of every project.
          </h3>
          <p className="text-base md:text-lg text-white/50 leading-relaxed max-w-2xl mx-auto">
            We believe that a high-performing website is built on listening, clarity, and a genuine commitment to our clients' success.
          </p>
        </motion.div>

        {/* Values Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="values-grid">
          {values.map((value, index) => (
            <motion.div
              key={index}
              variants={scaleIn}
              className="glass-card p-8 text-center group"
              data-testid={`value-card-${index}`}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-white/5 rounded-xl mb-6 group-hover:bg-white/10 transition-colors duration-500 text-white/70 group-hover:text-white">
                {value.icon}
              </div>
              <h4 className="text-xl font-bold text-white mb-3">{value.title}</h4>
              <p className="text-white/50 leading-relaxed">{value.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Bottom Statement */}
        <motion.p 
          variants={fadeInUp}
          className="text-center text-white/40 mt-12 max-w-2xl mx-auto text-sm md:text-base leading-relaxed"
        >
          Each project is designed with rigor, integrity, and attention to detail, to create websites that inspire confidence and deliver real results.
        </motion.p>
      </div>
    </AnimatedSection>
  );
};

// Agency Authority Section
const AgencyAuthority = () => {
  return (
    <AnimatedSection className="section relative overflow-hidden" id="about" data-testid="authority-section">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-[#050505]/70 z-10" />
        <img
          src="https://images.unsplash.com/photo-1771928258114-b345abf3f7b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBjcmVhdGl2ZSUyMG9mZmljZSUyMGRhcmslMjBhZXN0aGV0aWMlMjBtZWV0aW5nfGVufDB8fHx8MTc3MzAyNjA1MHww&ixlib=rb-4.1.0&q=85"
          alt="Luxury modern office"
          className="w-full h-full object-cover opacity-40 grayscale"
        />
      </div>

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="max-w-3xl">
          <motion.p variants={fadeInUp} className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6">
            Our Philosophy
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white mb-8"
            data-testid="authority-headline"
          >
            Designed to make businesses look
            <span className="font-accent italic"> established</span>
            <span className="text-white/40"> from the first second</span>
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-base md:text-lg text-white/50 leading-relaxed max-w-xl"
            data-testid="authority-text"
          >
            Webcraft builds websites that visually position businesses at a higher level. 
            We understand that perception drives decisions—and we engineer every pixel to 
            communicate success, trust, and authority.
          </motion.p>
        </div>
      </div>
    </AnimatedSection>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <AnimatedSection className="section bg-[#0A0A0A]" data-testid="cta-section">
      <div className="container mx-auto px-6 md:px-12 text-center">
        <motion.p variants={fadeInUp} className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6">
          Ready to Elevate?
        </motion.p>
        <motion.h2
          variants={fadeInUp}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-8 max-w-4xl mx-auto"
          data-testid="cta-headline"
        >
          Your business already deserves better than an
          <span className="font-accent italic"> average</span> website.
        </motion.h2>
        <motion.div variants={fadeInUp}>
          <motion.a
            href="#contact"
            className="cta-button inline-flex"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            data-testid="cta-button"
          >
            Request a Private Preview
            <ArrowRight size={18} />
          </motion.a>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    business_name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message);
      setFormData({ name: "", business_name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: <MapPin size={20} />, label: "Location", value: "York, Ontario, Canada" },
    { icon: <Phone size={20} />, label: "Phone", value: "(581) 984-8633", href: "tel:+15819848633" },
    { icon: <Mail size={20} />, label: "Email", value: "webcraftbusinesses@gmail.com", href: "mailto:webcraftbusinesses@gmail.com" },
    { icon: <Clock size={20} />, label: "Availability", value: "Open 24/7" }
  ];

  return (
    <AnimatedSection className="section bg-[#050505]" id="contact" data-testid="contact-section">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Get In Touch</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white" data-testid="contact-headline">
            Let's Create Something
            <span className="font-accent italic"> Extraordinary</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <motion.div variants={fadeInUp} className="space-y-8" data-testid="contact-info">
            <p className="text-white/50 text-lg leading-relaxed">
              Ready to transform your online presence? Reach out and let's discuss how 
              we can elevate your business to the next level.
            </p>
            
            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <div key={index} className="flex items-center gap-4" data-testid={`contact-${item.label.toLowerCase()}`}>
                  <div className="w-12 h-12 flex items-center justify-center bg-white/5 rounded-xl text-white/60">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-white/40 mb-1">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-white hover:text-white/80 transition-colors">
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-white">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Media Links */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-xs uppercase tracking-[0.15em] text-white/40 mb-4">Follow Us</p>
              <div className="flex items-center gap-4">
                <a 
                  href="https://www.facebook.com/share/1FM8AcUdKJ/?mibextid=wwXIfr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center bg-white/5 rounded-xl hover:bg-white/10 transition-colors group"
                  data-testid="contact-facebook-link"
                >
                  <svg className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.tiktok.com/@webcraft27?_r=1&_t=ZS-94XE63OrZ2I" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center bg-white/5 rounded-xl hover:bg-white/10 transition-colors group"
                  data-testid="contact-tiktok-link"
                >
                  <svg className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div variants={fadeInUp} data-testid="contact-form-container">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your Name"
                  required
                  className="form-input"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleInputChange}
                  placeholder="Business Name"
                  required
                  className="form-input"
                  data-testid="contact-business-input"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email Address"
                  required
                  className="form-input"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your project..."
                  required
                  rows={5}
                  className="form-input resize-none"
                  data-testid="contact-message-input"
                />
              </div>
              <motion.button
                type="submit"
                className="cta-button w-full justify-center"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  <>
                    Send Message
                    <ArrowRight size={18} />
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-20 bg-[#050505] border-t border-white/5" data-testid="footer">
      <div className="container mx-auto px-6 md:px-12">
        {/* Large Logo */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="mb-12"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_prestige-digital-1/artifacts/l5w9fjoc_WhatsApp%20Image%202026-03-08%20at%2023.43.08.jpeg" 
            alt="Webcraft" 
            className="h-12 sm:h-16 md:h-20 w-auto opacity-30"
          />
        </motion.div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          <div>
            <p className="text-white/40 text-sm mb-2">Premium Website Design Agency</p>
            <p className="text-white/30 text-sm">York, Ontario, Canada</p>
            
            {/* Social Media Links */}
            <div className="flex items-center gap-4 mt-4">
              <a 
                href="https://www.facebook.com/share/1FM8AcUdKJ/?mibextid=wwXIfr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-lg hover:bg-white/10 transition-colors group"
                data-testid="facebook-link"
              >
                <svg className="w-5 h-5 text-white/50 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="https://www.tiktok.com/@webcraft27?_r=1&_t=ZS-94XE63OrZ2I" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-lg hover:bg-white/10 transition-colors group"
                data-testid="tiktok-link"
              >
                <svg className="w-5 h-5 text-white/50 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8">
            <a href="tel:+15819848633" className="text-white/50 hover:text-white transition-colors text-sm">
              (581) 984-8633
            </a>
            <a href="mailto:webcraftbusinesses@gmail.com" className="text-white/50 hover:text-white transition-colors text-sm">
              webcraftbusinesses@gmail.com
            </a>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-sm">
            © {new Date().getFullYear()} Webcraft. All rights reserved.
          </p>
          <a 
            href="/admin" 
            className="text-white/20 hover:text-white/40 transition-colors text-xs"
            data-testid="admin-link"
          >
            Admin
          </a>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page
const LandingPage = () => {
  return (
    <div className="bg-[#050505] min-h-screen" data-testid="landing-page">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#FFFFFF',
          }
        }}
      />
      <Header />
      <HeroSection />
      <PremiumStatement />
      <WhoWeAreSection />
      <ServicesSection />
      <PortfolioSection />
      <TestimonialsSection />
      <WhyChooseSection />
      <OurValuesSection />
      <AgencyAuthority />
      <CTASection />
      <ContactSection />
      <Footer />
    </div>
  );
};

export default LandingPage;
